/* global google */
import React from 'react';
import PropTypes from 'prop-types';

import { InfoWindow, Marker } from 'react-google-maps';

import { Card, IconList, MapComponent } from 'common/components';
import { MAP_PINS } from 'common/components/MapComponent/constants';
import CustomControl from 'common/components/MapComponent/CustomControl';
import GeojsonFeature from 'common/components/MapComponent/GeojsonFeature';
import { coordsToLatLng } from 'common/components/MapComponent/util';

import { MAX_FEATURES_ON_MAP } from '.';

const EXISTING_FIELD_FILL_COLOR = '#008543';
const EXISTING_FIELD_FILL_OPACITY = 0.5;
const EXISTING_FIELD_STROKE_COLOR = '#ededed';
const EXISTING_FIELD_STROKE_OPACITY = 0.5;
const EXISTING_FIELD_STROKE_WEIGHT = 1;

const NEW_FIELD_FILL_COLOR = '#ffe35e';
const NEW_FIELD_FILL_OPACITY = 0.8;
const NEW_FIELD_STROKE_COLOR = '#ead10e';
const NEW_FIELD_STROKE_OPACITY = 0.8;
const NEW_FIELD_STROKE_WEIGHT = 1;

const colorAndOpacityToHex = (color, opacity = 1) =>
  `${color}${opacity >= 1 ? '' : Math.round(opacity * 255).toString(16)}`;

const Map = ({
  existingFields,
  features,
  filteredFeatures,
  mapRef,
  setFeatures,
}) => (
  <MapComponent
    className="uploadfield__column"
    ignoreMapUpdate
    usePreservedLocation
    mapRef={mapRef}
  >
    {/* Existing fields */}
    {existingFields.map(field => (
      <GeojsonFeature
        key={field.id}
        feature={field.geojson}
        options={{
          clickable: false,
          fillColor: EXISTING_FIELD_FILL_COLOR,
          fillOpacity: EXISTING_FIELD_FILL_OPACITY,
          strokeColor: EXISTING_FIELD_STROKE_COLOR,
          strokeWeight: EXISTING_FIELD_STROKE_WEIGHT,
          strokeOpacity: EXISTING_FIELD_STROKE_OPACITY,
        }}
      />
    ))}

    {/* Imported features */}
    {filteredFeatures
      .slice(0, MAX_FEATURES_ON_MAP)
      .valueSeq()
      .toJS()
      .map(feature => (
        <React.Fragment key={feature.id}>
          <GeojsonFeature
            feature={feature.geojson}
            visible={feature.checked || feature.hovering || feature.editingArea}
            options={{
              fillColor: NEW_FIELD_FILL_COLOR,
              fillOpacity: NEW_FIELD_FILL_OPACITY * (feature.checked ? 1 : 0.5),
              strokeColor: NEW_FIELD_STROKE_COLOR,
              strokeWeight: NEW_FIELD_STROKE_WEIGHT,
              strokeOpacity: NEW_FIELD_STROKE_OPACITY,
              zIndex: feature.hovering || feature.editingArea ? 2 : 1,
            }}
          />
          <Marker
            position={coordsToLatLng(feature.point.geometry.coordinates)}
            visible={feature.checked || feature.hovering || feature.editingArea}
            icon={{
              url: MAP_PINS['field'],
              anchor: new google.maps.Point(35, 60),
            }}
            onMouseOver={() => {
              setFeatures(features.setIn([feature.id, 'hovering'], true));
            }}
            onMouseOut={() => {
              setFeatures(features.setIn([feature.id, 'hovering'], false));
            }}
          >
            {(feature.hovering || feature.editingArea) && (
              <InfoWindow
                options={{
                  disableAutoPan: true,
                }}
              >
                <Card>
                  {feature.name && (
                    <h5 className={'cardlist__title'}>{feature.name}</h5>
                  )}
                  <IconList
                    list={[
                      {
                        label: `${feature.area} acres`,
                        icon: 'fields',
                      },
                    ]}
                  />
                </Card>
              </InfoWindow>
            )}
          </Marker>
        </React.Fragment>
      ))}

    {/* Legend */}
    {window.google && mapRef && mapRef.current && (
      <CustomControl controlPosition={google.maps.ControlPosition.TOP_LEFT}>
        <div className="uploadfield__legend">
          <div className="uploadfield__legend__entry">
            <span
              className="uploadfield__legend__swatch"
              style={{
                backgroundColor: colorAndOpacityToHex(
                  EXISTING_FIELD_FILL_COLOR,
                  EXISTING_FIELD_FILL_OPACITY
                ),
                borderColor: colorAndOpacityToHex(
                  EXISTING_FIELD_STROKE_COLOR,
                  EXISTING_FIELD_STROKE_OPACITY
                ),
                borderWidth: EXISTING_FIELD_STROKE_WEIGHT,
              }}
            />
            Existing Fields
          </div>
          <div className="uploadfield__legend__entry">
            <span
              className="uploadfield__legend__swatch"
              style={{
                backgroundColor: colorAndOpacityToHex(
                  NEW_FIELD_FILL_COLOR,
                  NEW_FIELD_FILL_OPACITY
                ),
                borderColor: colorAndOpacityToHex(
                  NEW_FIELD_STROKE_COLOR,
                  NEW_FIELD_STROKE_OPACITY
                ),
                borderWidth: NEW_FIELD_STROKE_WEIGHT,
              }}
            />
            New Fields
          </div>
        </div>
      </CustomControl>
    )}
  </MapComponent>
);
Map.propTypes = {
  existingFields: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  features: PropTypes.object.isRequired,
  filteredFeatures: PropTypes.object.isRequired,
  mapRef: PropTypes.object.isRequired,
  setFeatures: PropTypes.func.isRequired,
};

export default Map;



// WEBPACK FOOTER //
// ./src/pages/Field/Upload/Map.js